import React, { useState, useEffect } from 'react';
import MarkdownIt from 'markdown-it';
import MdEditor from 'react-markdown-editor-lite';
import 'react-markdown-editor-lite/lib/index.css';
import axios from '../utils/axiosConfig'; // CSRF設定
import API_URL from '../config';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const mdParser = new MarkdownIt();

function AdminCreateArticle() {
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState('');
  // 原有的英文內容 state 已經移除，下方新增英文各欄位的 state
  // 新增：英文標題
  const [engTitle, setEngTitle] = useState('');
  // 新增：英文摘要
  const [engSummary, setEngSummary] = useState('');
  // 新增：英文內容
  const [engContent, setEngContent] = useState('');
  // 新增：英文標籤
  const [engTags, setEngTags] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [uploadedImages, setUploadedImages] = useState([]);

  const extractImageUrls = (content) => {
    // 正則：匹配 Markdown 圖片語法 ![alt](url)
    const regex = /!\[[^\]]*\]\((.*?)\)/g;
    let urls = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      urls.push(match[1]);
    }
    return urls;
  };

  // 修改：同時從中文內容與英文內容中提取圖片 URL
  useEffect(() => {
    const chineseImageUrls = extractImageUrls(content);
    const englishImageUrls = extractImageUrls(engContent); // 新增：英文內容圖片
    const currentImageUrls = [...chineseImageUrls, ...englishImageUrls];
    // 找出 uploadedImages 中不再出現的圖片
    const imagesToDelete = uploadedImages.filter(url => !currentImageUrls.includes(url));
    
    imagesToDelete.forEach(async (url) => {
      try {
        await axios.delete(`${API_URL}/api/delete_image`, {
          data: { imageUrl: url },
          withCredentials: true,
        });
        console.log(`已刪除圖片: ${url}`);
        // 更新 state，移除已刪除的圖片
        setUploadedImages(prev => prev.filter(item => item !== url));
      } catch (error) {
        console.error(`刪除圖片失敗: ${url}`, error);
      }
    });
  }, [content, engContent]); // 修改：加入 engContent 為依賴

  // 圖片上傳到 AWS S3
  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
  
    try {
      const response = await axios.post(`${API_URL}/api/upload_images`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true, // 確保 CSRF 保護
      });
  
      const imageUrl = response.data.url; // 獲取 S3 回傳的圖片 URL
  
      // 將上傳成功的圖片 URL 存入 state
      setUploadedImages((prevImages) => [...prevImages, imageUrl]);
  
      return imageUrl;
    } catch (error) {
      console.error("圖片上傳失敗:", error);
      return "";
    }
  };
  
  const deleteUploadedImages = async () => {
    for (const imageUrl of uploadedImages) {
      try {
        await axios.delete(`${API_URL}/api/delete_image`, {
          data: { imageUrl: imageUrl },
          withCredentials: true,
        });
        console.log(`已刪除圖片: ${imageUrl}`);
      } catch (error) {
        console.error(`刪除圖片失敗: ${imageUrl}`, error);
      }
    }
    // 刪除完後，清空 state
    setUploadedImages([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const tagsArray = tags.split(',').map((tag) => tag.trim());
      
      // 新增：處理英文標籤，若後端需要陣列請使用 split(',') 處理
      const engTagsArray = engTags.split(',').map(tag => tag.trim());

      await axios.post(
        `${API_URL}/api/admin/articles`,
        { 
          title, 
          summary, 
          content, 
          tags: tagsArray, 
          translations: { 
            en: {
              title: engTitle,       // 新增：英文標題
              summary: engSummary,   // 新增：英文摘要
              content: engContent,   // 新增：英文內容
              tags: engTagsArray     // 新增：英文標籤
            } 
          } 
        },
        { withCredentials: true }
      );

      setSuccess(t('articleCreatedSuccess'));
      setTimeout(() => navigate('/admin/articles'), 1500);
    } catch (err) {
      console.error('創建文章時發生錯誤：', err);
      setError(t('errorCreatingArticle'));
    }
  };

  // 新增 handleCancel 函數
  const handleCancel = async () => {
    // 先等待圖片刪除完成
    await deleteUploadedImages();
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 text-black dark:text-white">
      <div className="container mx-auto px-4 py-6">
        <h2 className="text-2xl font-bold mb-4">{t('createArticle')}</h2>

        {error && <p className="text-red-500 mb-4">{error}</p>}
        {success && <p className="text-green-500 mb-4">{success}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 中文版文章欄位 */}
          <div>
            <label className="block mb-1">{t('title')}</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block mb-1">{t('summary')}</label>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div>
            <label className="block mb-1">{t('content')}</label>
            <MdEditor
              value={content}
              style={{ height: '500px' }}
              renderHTML={(text) => mdParser.render(text)}
              onChange={({ text }) => setContent(text)}
              onImageUpload={handleImageUpload}
            />
          </div>
          <div>
            <label className="block mb-1">{t('tags')}</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder={t('tagsPlaceholder')}
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>

          <hr className="my-6" />
          <h3 className="text-xl font-bold mb-4">英文版文章</h3>

          {/* 新增：英文版文章欄位 */}
          <div>
            <label className="block mb-1">英文標題</label> {/* 新增：英文標題 */}
            <input
              type="text"
              value={engTitle}
              onChange={(e) => setEngTitle(e.target.value)}
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
              required
            />
          </div>
          <div>
            <label className="block mb-1">英文摘要</label> {/* 新增：英文摘要 */}
            <textarea
              value={engSummary}
              onChange={(e) => setEngSummary(e.target.value)}
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>
          <div>
            <label className="block mb-1">英文內容</label> {/* 新增：英文內容 */}
            <MdEditor
              value={engContent}
              style={{ height: '500px' }}
              renderHTML={(text) => mdParser.render(text)}
              onChange={({ text }) => setEngContent(text)}
              onImageUpload={handleImageUpload}
            />
          </div>
          <div>
            <label className="block mb-1">英文標籤</label> {/* 新增：英文標籤 */}
            <input
              type="text"
              value={engTags}
              onChange={(e) => setEngTags(e.target.value)}
              placeholder="請以逗號分隔標籤"
              className="w-full p-2 border rounded bg-white dark:bg-gray-800 dark:text-white"
            />
          </div>

          <div className="flex space-x-4">
            <button
              type="button"
              onClick={handleCancel}
              className="bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
            >
              取消發文
            </button>

            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              {t('submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminCreateArticle;
